import { createTheme } from 'flowbite-react';

/**
 * Chronomap Flowbite theme — remaps Flowbite's default gray palette
 * to our neon dark aesthetic. Uses cm-* CSS custom property classes
 * for dark mode, with reasonable light mode fallbacks.
 */
export const chronomapTheme = createTheme({
  card: {
    root: {
      base: 'flex rounded-xl border border-cm-border-subtle bg-cm-bg-surface shadow-md',
      children: 'flex h-full flex-col justify-center gap-4 p-6',
    },
  },
  textInput: {
    field: {
      input: {
        colors: {
          gray: 'border-cm-border-subtle bg-cm-bg-elevated text-cm-text-primary placeholder-cm-text-muted focus:border-cm-accent-orange focus:ring-cm-accent-orange',
        },
      },
    },
  },
  textarea: {
    colors: {
      gray: 'border-cm-border-subtle bg-cm-bg-elevated text-cm-text-primary placeholder-cm-text-muted focus:border-cm-accent-orange focus:ring-cm-accent-orange',
    },
  },
  select: {
    field: {
      select: {
        colors: {
          gray: 'border-cm-border-subtle bg-cm-bg-elevated text-cm-text-primary focus:border-cm-accent-orange focus:ring-cm-accent-orange',
        },
      },
    },
  },
  label: {
    root: {
      colors: {
        default: 'text-cm-text-primary',
      },
    },
  },
  button: {
    color: {
      gray: 'border border-cm-border-subtle bg-cm-bg-elevated text-cm-text-primary hover:bg-cm-bg-hover',
      light: 'border border-cm-border-subtle bg-cm-bg-elevated text-cm-text-primary hover:bg-cm-bg-hover',
      dark: 'border border-cm-border-mid bg-cm-bg-surface text-cm-text-primary hover:bg-cm-bg-hover',
    },
  },
  modal: {
    root: {
      base: 'fixed inset-x-0 top-0 z-50 h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full',
    },
    content: {
      inner: 'relative flex max-h-[90dvh] flex-col rounded-xl bg-cm-bg-surface border border-cm-border-mid shadow-2xl',
    },
    header: {
      base: 'flex items-start justify-between rounded-t border-b border-cm-border-subtle p-5',
      title: 'text-xl font-mono font-semibold text-cm-text-primary',
      close: {
        base: 'ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-cm-text-muted hover:bg-cm-bg-hover hover:text-cm-text-primary',
      },
    },
    body: {
      base: 'flex-1 overflow-auto p-6',
    },
    footer: {
      base: 'flex items-center space-x-2 rounded-b border-t border-cm-border-subtle p-6',
    },
  },
  alert: {
    root: {
      base: 'flex flex-col gap-2 rounded-xl border p-4 text-sm',
      color: {
        info: 'border-cm-accent-blue/30 bg-cm-accent-blue/10 text-cm-accent-blue',
        failure: 'border-red-500/30 bg-red-500/10 text-red-400',
        success: 'border-cm-accent-green/30 bg-cm-accent-green/10 text-cm-accent-green',
        warning: 'border-cm-accent-gold/30 bg-cm-accent-gold/10 text-cm-accent-gold',
      },
    },
  },
  badge: {
    root: {
      color: {
        info: 'bg-cm-accent-blue/15 text-cm-accent-blue',
        gray: 'bg-cm-bg-elevated text-cm-text-secondary',
        success: 'bg-cm-accent-green/15 text-cm-accent-green',
        failure: 'bg-red-500/15 text-red-400',
        warning: 'bg-cm-accent-gold/15 text-cm-accent-gold',
      },
    },
  },
  tooltip: {
    target: '',
    animation: 'transition-opacity',
    base: 'absolute z-10 inline-block rounded-lg px-3 py-2 text-sm font-medium shadow-sm',
    style: {
      dark: 'bg-cm-bg-elevated text-cm-text-primary border border-cm-border-mid',
      light: 'border border-cm-border-subtle bg-cm-bg-surface text-cm-text-primary',
      auto: 'border border-cm-border-mid bg-cm-bg-elevated text-cm-text-primary',
    },
  },
  spinner: {
    color: {
      info: 'fill-cm-accent-orange',
    },
  },
  tabs: {
    tablist: {
      base: 'flex text-center',
      tabitem: {
        base: 'flex items-center justify-center rounded-t-lg p-4 text-sm font-mono font-medium',
        variant: {
          default: {
            base: 'rounded-t-lg',
            active: {
              on: 'bg-cm-bg-elevated text-cm-accent-orange',
              off: 'text-cm-text-muted hover:bg-cm-bg-hover hover:text-cm-text-secondary',
            },
          },
          underline: {
            base: 'rounded-t-lg',
            active: {
              on: 'active rounded-t-lg border-b-2 border-cm-accent-orange text-cm-accent-orange',
              off: 'border-b-2 border-transparent text-cm-text-muted hover:border-cm-border-mid hover:text-cm-text-secondary',
            },
          },
        },
      },
    },
  },
  table: {
    root: {
      base: 'w-full text-left text-sm text-cm-text-secondary',
      shadow: '',
    },
    head: {
      base: 'text-xs uppercase text-cm-text-muted',
      cell: {
        base: 'bg-cm-bg-elevated px-6 py-3',
      },
    },
    body: {
      base: 'divide-y divide-cm-border-subtle',
      cell: {
        base: 'px-6 py-4',
      },
    },
    row: {
      base: 'bg-cm-bg-surface',
      hovered: 'hover:bg-cm-bg-hover',
    },
  },
});
